import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api, useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  ArrowLeft, Plus, Pencil, Trash2, ClipboardCheck, Repeat,
  CalendarDays, CheckCircle2, Clock, Circle, AlertCircle, Sparkles,
  Sunrise, Sunset, Sun, Wand2, Settings2, RefreshCw, Printer,
} from "lucide-react";
import { toast } from "sonner";
import { TRADES, tradeMeta } from "@/pages/Cleaners";

const PRIORITY_META = {
  low:    { label: "Low",    color: "bg-slate-100 text-slate-700 border-slate-200" },
  normal: { label: "Normal", color: "bg-blue-50 text-blue-800 border-blue-200" },
  high:   { label: "High",   color: "bg-amber-100 text-amber-900 border-amber-300" },
  urgent: { label: "Urgent", color: "bg-red-100 text-red-800 border-red-200" },
};

const STATUS_META = {
  pending:     { label: "Pending",     color: "bg-slate-100 text-slate-700 border-slate-200", Icon: Circle },
  in_progress: { label: "In progress", color: "bg-amber-100 text-amber-900 border-amber-300", Icon: Clock },
  done:        { label: "Done",        color: "bg-emerald-100 text-emerald-800 border-emerald-200", Icon: CheckCircle2 },
};

import { parseLocalDate, todayLocalIso, todayInOrgIso } from "@/lib/dateUtils";

const SLOT_META = {
  morning:   { label: "Morning",   short: "AM",  color: "bg-amber-50 text-amber-800 border-amber-200",   Icon: Sunrise },
  afternoon: { label: "Afternoon", short: "PM",  color: "bg-orange-50 text-orange-800 border-orange-200", Icon: Sunset },
  all_day:   { label: "All day",   short: "ALL", color: "bg-sky-50 text-sky-800 border-sky-200",          Icon: Sun },
};

const DOW = [
  { k: 0, label: "Mon" }, { k: 1, label: "Tue" }, { k: 2, label: "Wed" },
  { k: 3, label: "Thu" }, { k: 4, label: "Fri" }, { k: 5, label: "Sat" }, { k: 6, label: "Sun" },
];

const todayIso = () => todayLocalIso();
const orgToday = (tz) => todayInOrgIso(tz);
const blankDraft = () => ({
  id: null,
  title: "",
  description: "",
  helper_id: "__unassigned__",
  unit_id: "__none__",
  kind: "one_off",
  date: todayIso(),
  start_time: "",
  end_time: "",
  slot: "all_day",
  priority: "normal",
  status: "pending",
  category: "housekeeping",
  notes: "",
  recurrence: { type: "weekly", days_of_week: [0, 1, 2, 3, 4], starts_on: todayIso(), ends_on: "" },
});

export default function HelperTasks() {
  const navigate = useNavigate();
  const { orgTimezone } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [helpers, setHelpers] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterHelper, setFilterHelper] = useState("__all__");
  const [filterStatus, setFilterStatus] = useState("__all__");
  const [filterSource, setFilterSource] = useState("__all__"); // __all__ | manual | auto
  const [draft, setDraft] = useState(null);  // dialog open when not null
  const [saving, setSaving] = useState(false);
  const [suggestions, setSuggestions] = useState(null);  // {date, slot, helpers[]}
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tk, cl, un] = await Promise.all([
        api.get("/housing/helper-tasks"),
        api.get("/housing/cleaners"),
        api.get("/housing/units"),
      ]);
      setTasks(tk.data);
      setHelpers(cl.data);
      setUnits(un.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Phase 44 — fetch live suggestions whenever the dialog's date / slot changes
  useEffect(() => {
    if (!draft || draft.kind !== "one_off" || !draft.date) {
      setSuggestions(null); return;
    }
    let cancelled = false;
    setSuggestLoading(true);
    api.get(`/housing/helper-tasks/suggest-assignee?date=${draft.date}&slot=${draft.slot || "all_day"}`)
      .then((res) => { if (!cancelled) setSuggestions(res.data); })
      .catch(() => { if (!cancelled) setSuggestions(null); })
      .finally(() => { if (!cancelled) setSuggestLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft?.date, draft?.slot, draft?.kind]);

  const regenerate = async () => {
    setRegenerating(true);
    try {
      await api.post("/housing/helper-tasks/regenerate");
      toast.success("Auto-tasks regenerated for the next 60 days");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Regenerate failed");
    } finally { setRegenerating(false); }
  };

  const helperById = useMemo(() => Object.fromEntries(helpers.map((h) => [h.id, h])), [helpers]);
  const unitById = useMemo(() => Object.fromEntries(units.map((u) => [u.id, u])), [units]);

  const filtered = useMemo(() => {
    return tasks.filter((t) => {
      if (filterHelper !== "__all__") {
        if (filterHelper === "__unassigned__" && t.helper_id) return false;
        if (filterHelper !== "__unassigned__" && t.helper_id !== filterHelper) return false;
      }
      if (filterStatus !== "__all__") {
        if (t.kind === "one_off" && t.status !== filterStatus) return false;
        if (t.kind === "recurring" && filterStatus !== "pending") return false;
      }
      if (filterSource === "auto" && !t.auto_source) return false;
      if (filterSource === "manual" && t.auto_source) return false;
      return true;
    });
  }, [tasks, filterHelper, filterStatus, filterSource]);

  // Phase 44 — group one-off tasks by date for the unified list view.
  const groupedByDate = useMemo(() => {
    const groups = new Map();
    const recurring = [];
    filtered.forEach((t) => {
      if (t.kind === "recurring") { recurring.push(t); return; }
      const k = t.date || "—";
      if (!groups.has(k)) groups.set(k, []);
      groups.get(k).push(t);
    });
    const sorted = Array.from(groups.entries()).sort(([a], [b]) => a.localeCompare(b));
    return { sorted, recurring };
  }, [filtered]);

  // Counts for the source pills
  const sourceCounts = useMemo(() => {
    const auto = tasks.filter((t) => t.auto_source).length;
    return { all: tasks.length, manual: tasks.length - auto, auto };
  }, [tasks]);

  const openNew = () => setDraft({ ...blankDraft(), date: orgToday(orgTimezone) });
  const openEdit = (t) => setDraft({
    ...t,
    helper_id: t.helper_id || "__unassigned__",
    unit_id: t.unit_id || "__none__",
    slot: t.slot || "all_day",
    recurrence: t.recurrence || blankDraft().recurrence,
  });

  const save = async () => {
    if (!draft.title.trim()) { toast.error("Title required"); return; }
    if (draft.kind === "one_off" && !draft.date) { toast.error("Pick a date"); return; }
    if (draft.kind === "recurring") {
      if (draft.recurrence?.type === "weekly" && !(draft.recurrence.days_of_week?.length)) {
        toast.error("Pick at least one day of the week"); return;
      }
      if (!draft.recurrence?.starts_on) { toast.error("Pick a start date"); return; }
    }
    setSaving(true);
    const body = {
      title: draft.title.trim(),
      description: draft.description || "",
      helper_id: draft.helper_id === "__unassigned__" ? null : draft.helper_id,
      unit_id: draft.unit_id === "__none__" ? null : draft.unit_id,
      kind: draft.kind,
      date: draft.kind === "one_off" ? draft.date : null,
      start_time: draft.start_time || "",
      end_time: draft.end_time || "",
      slot: draft.slot || "all_day",
      priority: draft.priority || "normal",
      status: draft.status || "pending",
      category: draft.category || "housekeeping",
      notes: draft.notes || "",
      recurrence: draft.kind === "recurring" ? {
        type: draft.recurrence.type,
        days_of_week: draft.recurrence.type === "weekly" ? (draft.recurrence.days_of_week || []) : [],
        starts_on: draft.recurrence.starts_on,
        ends_on: draft.recurrence.ends_on || null,
      } : null,
    };
    try {
      if (draft.id) {
        await api.put(`/housing/helper-tasks/${draft.id}`, body);
        toast.success("Task updated");
      } else {
        await api.post("/housing/helper-tasks", body);
        toast.success("Task created");
      }
      setDraft(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const del = async (t) => {
    if (!window.confirm(`Delete task "${t.title}"? ${t.kind === "recurring" ? "All recurrence history will be lost." : ""}`)) return;
    try {
      await api.delete(`/housing/helper-tasks/${t.id}`);
      toast.success("Task deleted");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const quickStatus = async (t, newStatus) => {
    if (t.kind === "recurring") {
      toast.info("Status on recurring tasks is tracked per day — mark done from the Helpers Calendar or Daily Worksheet");
      return;
    }
    try {
      await api.put(`/housing/helper-tasks/${t.id}/status?status=${newStatus}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };

  const describeRecurrence = (t) => {
    if (t.kind !== "recurring" || !t.recurrence) return "";
    const r = t.recurrence;
    if (r.type === "daily") return `Every day · from ${r.starts_on}${r.ends_on ? ` → ${r.ends_on}` : ""}`;
    if (r.type === "weekly") {
      const names = (r.days_of_week || []).map((k) => DOW[k]?.label).join(", ");
      return `Weekly on ${names} · from ${r.starts_on}${r.ends_on ? ` → ${r.ends_on}` : ""}`;
    }
    return "";
  };

  const toggleDow = (k) => {
    setDraft((d) => {
      const set = new Set(d.recurrence.days_of_week || []);
      if (set.has(k)) set.delete(k); else set.add(k);
      return { ...d, recurrence: { ...d.recurrence, days_of_week: [...set].sort() } };
    });
  };

  // Phase 44 — single card renderer for both recurring + one-off lists.
  const renderTaskCard = (t, isRecurring) => {
    const statusKey = t.kind === "one_off" ? (t.status || "pending") : "pending";
    const StatusIcon = STATUS_META[statusKey]?.Icon || Circle;
    const slot = SLOT_META[t.slot || "all_day"];
    const SlotIcon = slot?.Icon || Sun;
    const isAuto = !!t.auto_source;
    return (
      <div
        key={t.id}
        className={`p-3 rounded-lg border flex items-start gap-3 hover:shadow-sm transition ${
          isAuto ? "bg-emerald-50/40 border-emerald-200" : "bg-white border-[#E8E8E6]"
        }`}
        data-testid={`task-row-${t.id}`}
      >
        {/* slot stripe */}
        <div className={`flex flex-col items-center justify-center px-2 py-1 rounded-md border text-[10px] font-bold ${slot?.color}`} title={slot?.label}>
          <SlotIcon className="h-4 w-4" />
          <span className="mt-0.5">{slot?.short}</span>
        </div>
        {/* main */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 flex-wrap">
            <p className="font-medium text-[#1C1F1E] flex items-center gap-1.5 flex-wrap">
              {isRecurring && <Repeat className="h-3.5 w-3.5 text-purple-700" />}
              {t.title}
              {isAuto && (
                <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-300 text-[10px] inline-flex items-center gap-1">
                  <Sparkles className="h-3 w-3" /> Auto
                </Badge>
              )}
              <Badge className={`${PRIORITY_META[t.priority || "normal"]?.color} border text-[10px]`}>
                {PRIORITY_META[t.priority || "normal"]?.label}
              </Badge>
              {(() => {
                const m = tradeMeta(t.category || "housekeeping");
                const Icon = m.icon;
                return (
                  <Badge className={`${m.color} border text-[10px] inline-flex items-center gap-1`} data-testid={`task-trade-${t.id}`}>
                    <Icon className="h-3 w-3" /> {m.label}
                  </Badge>
                );
              })()}
              {t.work_order_ref && (
                <Badge className="bg-slate-50 text-slate-700 border border-slate-200 text-[10px] font-mono" data-testid={`task-wo-${t.id}`}>{t.work_order_ref}</Badge>
              )}
              {!isRecurring && (
                <Badge className={`${STATUS_META[statusKey]?.color} border text-[10px] inline-flex items-center gap-1`}>
                  <StatusIcon className="h-3 w-3" />
                  {STATUS_META[statusKey]?.label}
                </Badge>
              )}
            </p>
            <div className="flex gap-0.5 shrink-0">
              {!isRecurring && t.status !== "done" && (
                <Button size="sm" variant="ghost" onClick={() => quickStatus(t, "done")} className="text-emerald-700 hover:bg-emerald-50 h-7 w-7 p-0" title="Mark done" data-testid={`done-${t.id}`}>
                  <CheckCircle2 className="h-3.5 w-3.5" />
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={() => openEdit(t)} className="text-[#1B4332] h-7 w-7 p-0" data-testid={`edit-${t.id}`}>
                <Pencil className="h-3.5 w-3.5" />
              </Button>
              <Button size="sm" variant="ghost" onClick={() => del(t)} className="text-red-600 h-7 w-7 p-0" data-testid={`delete-${t.id}`}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
          {t.description && <p className="text-[12px] text-[#525C58] mt-0.5 line-clamp-2">{t.description}</p>}
          <div className="flex items-center gap-3 mt-1 text-[11px] text-[#525C58] flex-wrap">
            {isRecurring ? (
              <span className="text-purple-800">{describeRecurrence(t)}</span>
            ) : (
              <>
                {(t.start_time || t.end_time) && (
                  <span className="text-[#7A8A84]"><Clock className="inline h-3 w-3 mr-0.5" />{t.start_time}{t.end_time ? ` → ${t.end_time}` : ""}</span>
                )}
              </>
            )}
            <span>
              {t.helper_id ? (
                <>👤 {helperById[t.helper_id]?.name || "?"}</>
              ) : (
                <Badge className="bg-amber-100 text-amber-900 border border-amber-300 text-[10px]">Unassigned</Badge>
              )}
            </span>
            {t.unit_id && unitById[t.unit_id] && (
              <span>📍 {unitById[t.unit_id]?.name}</span>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6 fade-in" data-testid="helper-tasks-page">
      <Button variant="ghost" onClick={() => navigate(-1)} className="text-[#525C58]" data-testid="back-btn">
        <ArrowLeft className="h-4 w-4 mr-2" /> Back
      </Button>

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-4xl font-semibold tracking-tight flex items-center gap-3" style={{ fontFamily: "Manrope, sans-serif" }}>
            <ClipboardCheck className="h-8 w-8 text-[#40916C]" /> Work Orders
          </h1>
          <p className="text-[#525C58] mt-1">One unified list — manual tasks, recurring chores and auto-generated checkout cleans, all in one place.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate(`/helper-tasks/print?date=${orgToday(orgTimezone)}`)} className="border-[#1B4332]/20" data-testid="print-roster-btn">
            <Printer className="h-4 w-4 mr-1" /> Print roster
          </Button>
          <Button variant="outline" size="sm" onClick={regenerate} disabled={regenerating} className="border-[#1B4332]/20" data-testid="regenerate-btn">
            <RefreshCw className={`h-4 w-4 mr-1 ${regenerating ? "animate-spin" : ""}`} />
            {regenerating ? "Regenerating…" : "Rebuild auto-tasks"}
          </Button>
          <Button variant="outline" size="sm" onClick={() => navigate("/helper-task-rules")} className="border-purple-300 text-purple-700" data-testid="rules-btn">
            <Settings2 className="h-4 w-4 mr-1" /> Auto-rules
          </Button>
          <Button onClick={openNew} className="bg-[#1B4332] hover:bg-[#2D6A4F]" data-testid="new-task-btn">
            <Plus className="h-4 w-4 mr-1" /> New task
          </Button>
        </div>
      </div>

      {/* Source pills */}
      <div className="flex flex-wrap gap-2" data-testid="source-pills">
        {[
          { k: "__all__", label: "All",      count: sourceCounts.all,    icon: ClipboardCheck },
          { k: "manual",  label: "Manual",   count: sourceCounts.manual, icon: Pencil },
          { k: "auto",    label: "Auto-generated", count: sourceCounts.auto, icon: Sparkles },
        ].map((p) => {
          const Icon = p.icon;
          const active = filterSource === p.k;
          return (
            <button
              key={p.k}
              type="button"
              onClick={() => setFilterSource(p.k)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                active ? "bg-[#1B4332] text-white border-[#1B4332]"
                       : "bg-white text-[#525C58] border-[#E8E8E6] hover:bg-[#F4F4F1]"
              }`}
              data-testid={`source-${p.k}`}
            >
              <Icon className="h-3.5 w-3.5" /> {p.label}
              <Badge className={`ml-1 ${active ? "bg-white/20 text-white" : "bg-[#F4F4F1] text-[#525C58]"} border-0 text-[10px]`}>{p.count}</Badge>
            </button>
          );
        })}
      </div>

      <Card className="border-[#E8E8E6]">
        <CardHeader>
          <CardTitle className="text-base flex items-center justify-between gap-3 flex-wrap">
            <span>All tasks ({filtered.length})</span>
            <div className="flex items-center gap-2 flex-wrap">
              <Select value={filterHelper} onValueChange={setFilterHelper}>
                <SelectTrigger className="w-44 text-sm" data-testid="filter-helper"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All helpers</SelectItem>
                  <SelectItem value="__unassigned__">Unassigned</SelectItem>
                  {helpers.filter((h) => h.active).map((h) => (
                    <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-40 text-sm" data-testid="filter-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All statuses</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="in_progress">In progress</SelectItem>
                  <SelectItem value="done">Done</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-[#7A8A84] text-center py-10">Loading…</p>
          ) : filtered.length === 0 ? (
            <div className="text-center py-14 text-[#7A8A84]">
              <ClipboardCheck className="h-10 w-10 mx-auto text-[#B8C2BC] mb-3" />
              <p>No work orders yet.</p>
              <Button variant="ghost" onClick={openNew} className="mt-2 text-[#1B4332]" data-testid="empty-new-btn">
                <Plus className="h-4 w-4 mr-1" /> Create the first one
              </Button>
            </div>
          ) : (
            <div className="space-y-6" data-testid="tasks-grouped">
              {groupedByDate.recurring.length > 0 && (
                <div data-testid="group-recurring">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-purple-700 mb-2 flex items-center gap-1.5">
                    <Repeat className="h-3.5 w-3.5" /> Recurring chores ({groupedByDate.recurring.length})
                  </h3>
                  <div className="space-y-2">
                    {groupedByDate.recurring.map((t) => renderTaskCard(t, true))}
                  </div>
                </div>
              )}
              {groupedByDate.sorted.map(([date, list]) => {
                const d = parseLocalDate(date);
                const human = isNaN(d.getTime()) ? date : d.toLocaleDateString(undefined, {
                  weekday: "short", year: "numeric", month: "short", day: "numeric",
                });
                const isToday = date === orgToday(orgTimezone);
                return (
                  <div key={date} data-testid={`group-${date}`}>
                    <h3 className={`text-xs font-semibold uppercase tracking-wide mb-2 flex items-center gap-1.5 ${
                      isToday ? "text-emerald-700" : "text-[#525C58]"
                    }`}>
                      <CalendarDays className="h-3.5 w-3.5" /> {human}
                      {isToday && <Badge className="bg-emerald-100 text-emerald-800 border-0 text-[10px]">Today</Badge>}
                      <span className="text-[#9AA39E] font-normal">— {list.length} task{list.length !== 1 ? "s" : ""}</span>
                    </h3>
                    <div className="space-y-2">
                      {list.map((t) => renderTaskCard(t, false))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create / Edit dialog */}
      <Dialog open={!!draft} onOpenChange={(o) => !o && setDraft(null)}>
        <DialogContent className="max-w-xl" data-testid="task-dialog">
          <DialogHeader>
            <DialogTitle>{draft?.id ? "Edit work order" : "New work order"}</DialogTitle>
            <DialogDescription>
              Ad-hoc tasks happen once; recurring tasks generate occurrences on the helper calendar and daily worksheet.
            </DialogDescription>
          </DialogHeader>
          {draft && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="md:col-span-2 space-y-1">
                  <Label>Title *</Label>
                  <Input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} data-testid="task-title" placeholder="e.g. Deep clean chapel, Laundry pickup" />
                </div>
                <div className="md:col-span-2 space-y-1">
                  <Label>Description</Label>
                  <Textarea rows={2} value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} data-testid="task-desc" />
                </div>

                <div className="space-y-1">
                  <Label className="flex items-center gap-1">
                    Helper
                    {suggestLoading && <span className="text-[10px] text-[#9AA39E]">(checking availability…)</span>}
                  </Label>
                  <Select value={draft.helper_id} onValueChange={(v) => setDraft({ ...draft, helper_id: v })}>
                    <SelectTrigger data-testid="task-helper"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__unassigned__">— Unassigned —</SelectItem>
                      {/* Phase 44 — smart-suggestion ranked list when a date+slot is set */}
                      {draft.kind === "one_off" && suggestions?.helpers?.length ? (
                        suggestions.helpers.map((s) => {
                          const dot = s.available ? "🟢" : (s.is_working_day ? "🟡" : "🔴");
                          return (
                            <SelectItem key={s.id} value={s.id}>
                              {dot} {s.name} — {s.reason}
                            </SelectItem>
                          );
                        })
                      ) : (
                        helpers.filter((h) => h.active).map((h) => (
                          <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  {draft.kind === "one_off" && suggestions?.helpers?.length > 0 && suggestions.helpers[0]?.available && (
                    <button
                      type="button"
                      className="text-[11px] text-emerald-700 hover:underline inline-flex items-center gap-1 mt-1"
                      onClick={() => setDraft({ ...draft, helper_id: suggestions.helpers[0].id })}
                      data-testid="suggest-pick-btn"
                    >
                      <Wand2 className="h-3 w-3" />
                      Auto-pick: {suggestions.helpers[0].name} ({suggestions.helpers[0].reason})
                    </button>
                  )}
                </div>
                <div className="space-y-1">
                  <Label>Unit (optional)</Label>
                  <Select value={draft.unit_id} onValueChange={(v) => setDraft({ ...draft, unit_id: v })}>
                    <SelectTrigger data-testid="task-unit"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— Not unit-specific —</SelectItem>
                      {units.filter((u) => u.active !== false).map((u) => (
                        <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1">
                  <Label>Priority</Label>
                  <Select value={draft.priority} onValueChange={(v) => setDraft({ ...draft, priority: v })}>
                    <SelectTrigger data-testid="task-priority"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(PRIORITY_META).map(([k, m]) => (
                        <SelectItem key={k} value={k}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Trade / category</Label>
                  <Select value={draft.category || "housekeeping"} onValueChange={(v) => setDraft({ ...draft, category: v })}>
                    <SelectTrigger data-testid="task-category"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TRADES.map((t) => (
                        <SelectItem key={t.k} value={t.k}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Kind</Label>
                  <Select value={draft.kind} onValueChange={(v) => setDraft({ ...draft, kind: v })}>
                    <SelectTrigger data-testid="task-kind"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="one_off">Ad-hoc (one-off)</SelectItem>
                      <SelectItem value="recurring">Recurring</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {draft.kind === "one_off" ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-3 bg-[#F4F4F1] rounded-lg border border-[#E8E8E6]">
                  <div className="space-y-1">
                    <Label>Date *</Label>
                    <Input type="date" value={draft.date || ""} onChange={(e) => setDraft({ ...draft, date: e.target.value })} data-testid="task-date" />
                  </div>
                  <div className="space-y-1">
                    <Label>Slot</Label>
                    <Select value={draft.slot || "all_day"} onValueChange={(v) => setDraft({ ...draft, slot: v })}>
                      <SelectTrigger data-testid="task-slot"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(SLOT_META).map(([k, m]) => (
                          <SelectItem key={k} value={k}>{m.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label>Start time</Label>
                    <Input type="time" value={draft.start_time || ""} onChange={(e) => setDraft({ ...draft, start_time: e.target.value })} data-testid="task-start" />
                  </div>
                  <div className="space-y-1">
                    <Label>End time</Label>
                    <Input type="time" value={draft.end_time || ""} onChange={(e) => setDraft({ ...draft, end_time: e.target.value })} data-testid="task-end" />
                  </div>
                </div>
              ) : (
                <div className="space-y-3 p-3 bg-purple-50/50 rounded-lg border border-purple-200" data-testid="recurrence-block">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label>Repeats</Label>
                      <Select value={draft.recurrence?.type || "weekly"} onValueChange={(v) => setDraft({ ...draft, recurrence: { ...draft.recurrence, type: v } })}>
                        <SelectTrigger data-testid="recur-type"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="daily">Every day</SelectItem>
                          <SelectItem value="weekly">Weekly (pick days)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  {draft.recurrence?.type === "weekly" && (
                    <div className="space-y-1">
                      <Label>Days of the week</Label>
                      <div className="flex flex-wrap gap-1.5" data-testid="dow-picker">
                        {DOW.map((d) => {
                          const on = (draft.recurrence.days_of_week || []).includes(d.k);
                          return (
                            <button key={d.k} type="button" onClick={() => toggleDow(d.k)} data-testid={`dow-${d.k}`}
                              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                                on
                                  ? "bg-purple-700 text-white border-purple-700"
                                  : "bg-white text-[#525C58] border-[#E8E8E6] hover:bg-[#F4F4F1]"
                              }`}>
                              {d.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label>Starts on *</Label>
                      <Input type="date" value={draft.recurrence?.starts_on || ""} onChange={(e) => setDraft({ ...draft, recurrence: { ...draft.recurrence, starts_on: e.target.value } })} data-testid="recur-starts" />
                    </div>
                    <div className="space-y-1">
                      <Label>Ends on (optional)</Label>
                      <Input type="date" value={draft.recurrence?.ends_on || ""} onChange={(e) => setDraft({ ...draft, recurrence: { ...draft.recurrence, ends_on: e.target.value } })} data-testid="recur-ends" />
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-1">
                <Label>Notes</Label>
                <Textarea rows={2} value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} data-testid="task-notes" />
              </div>

              {!draft.helper_id || draft.helper_id === "__unassigned__" ? (
                <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-[12px] text-amber-900">
                  <AlertCircle className="h-4 w-4 mt-0.5" />
                  <span>This task will be <strong>unassigned</strong>. You can drag it onto a helper from the Helpers Calendar.</span>
                </div>
              ) : null}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDraft(null)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-[#1B4332] hover:bg-[#2D6A4F]" data-testid="save-task-btn">
              {saving ? "Saving…" : (draft?.id ? "Save changes" : "Create task")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
